import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactAccountMangerButton from "@tIronValleySupply/Components/ContactAccountMangerButton";
import ProductInStockNotificationButton from "@tIronValleySupply/Components/ProductInStockNotificationButton";
import ViewProductButton from "@tIronValleySupply/Components/ViewProductButton";
import { ProductAvailabilityStyles } from "@tIronValleySupply/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@tIronValleySupply/Overrides/Components/ProductContextAvailability";
import CustomApplicationState from "@tIronValleySupply/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    cartLine: CartLineModel;
    cartLines: CartLineModel;
}

type MapStateToPropsReturnType = {
    session: any;
    productSettings: any; // Assuming productSettings has a specific type
    isAuthenticated: boolean;
    msrpValue: any;
};

const mapStateToProps = (state: CustomApplicationState): MapStateToPropsReturnType => {
    const session = getSession(state);
    const { productSettings } = getSettingsCollection(state);

    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const msrpValue = state.IronValleyData.MsrpValueStore.msrpValue;

    return {
        session,
        productSettings,
        isAuthenticated,
        msrpValue,
    };
};

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    priceContainer?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    btnWrapper?: InjectableCss;
    wrapperContact?: InjectableCss;
    labelTextDealerNormal?: TypographyProps;
    labelTextNormal?: TypographyProps;
    valueTextNormal?: TypographyProps;
    valueTextWarp?: TypographyProps;
    availabilityStyles?: ProductAvailabilityStyles;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapperContact: {
        css: css`
            display: flex;
            width: 100%;
            margin-top: 20px;
            flex-direction: row;
            align-items: end;
        `,
    },
    availabilityStyles: {
        messageGridItem: {
            css: css`
                > div {
                    justify-content: end;
                }
            `,
        },
        messageText: {
            css: css`
                width: auto;
            `,
        },
    },
    priceContainer: {
        css: css`
            margin-top: 30px;
            display: flex;
            justify-content: center;
            flex-direction: column;
            align-items: end;
            > section {
                width: auto;
                > div > span {
                    font-weight: 600;
                    font-size: 20.2735px;
                    line-height: 18px;
                    text-transform: uppercase;
                    margin-bottom: 5px;
                }
            }
            > p {
                margin-top: 0;
                > span {
                    font-weight: 600;
                    font-size: 20.2735px;
                    line-height: 18px;
                    text-transform: uppercase;
                    color: #e13e28;
                    margin-bottom: 5px;
                }
            }
        `,
    },
    labelTextNormal: {
        css: css`
            text-transform: uppercase;
            font-weight: 600;
            font-size: 12px;
            line-height: 17px;
            margin-right: 0;
            color: #252525;
            width: 36px;
        `,
    },
    labelTextDealerNormal: {
        css: css`
            text-transform: uppercase;
            font-weight: 600;
            font-size: 12px;
            line-height: 17px;
            margin-right: 0;
            color: #252525;
            width: 172px;
        `,
    },
    valueTextWarp: {
        size: 16,
        css: css`
            margin-top: 15px;
            display: flex;
            align-items: center;
        `,
    },
    valueTextNormal: {
        css: css`
            font-weight: 700;
            font-size: 12px;
            line-height: 19px;
            color: #e13e28;
            margin-left: 8px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    btnWrapper: {
        css: css`
            flex: 1;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;

            input[data-test-selector="product_qtyOrdered"] {
                width: 40px !important;
                text-align: center;
            }
            div[class^="FormFieldStyle"] {
                min-width: unset;
                width: unset;
                margin-right: 0;
            }

            button[data-test-selector^="actionsAddToCart"] {
                width: 100%;
            }
            button[data-test-selector^="actionsAddToList"] {
                width: 100%;
                margin-bottom: 0 !important;
            }
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
            padding: 0 6px !important;
            > span {
                font-weight: 400;
                font-weight: 500;
                font-size: 16px;
                text-transform: uppercase;
            }
        `,
    },
    addToListLink: {
        link: {
            css: css`
                > span {
                    padding: 4px 2px !important;
                }
            `,
        },
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
            button[data-test-selector^="actionsAddToList"] {
                width: 100%;
                margin-bottom: 0 !important;
            }
        `,
    },
};

const styles = actionsStyles;

const ProductListActionsListView: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    cartLines,
    showAddToList,
    isAuthenticated,
    session,
    msrpValue,
    cartLine,
}) => {
    let msrpValueToDisplay: string | null = "false";

    if (typeof window !== "undefined") {
        const storedValue = localStorage.getItem("msrpValue");
        msrpValueToDisplay = storedValue !== null ? storedValue : "false";
    }

    if (productContext.product.properties.allocated_Product === "True") {
        return (
            <StyledWrapper {...styles.wrapperContact}>
                <StyledWrapper {...styles.btnWrapper}>
                    <ContactAccountMangerButton />
                </StyledWrapper>
                <StyledWrapper>
                    {showAddToList && (
                        <StyledWrapper {...styles.addToListWrapper}>
                            <ProductAddToListLink
                                data-test-selector={`actionsAddToList${productContext.product.id}`}
                                extendedStyles={styles.addToListLink}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            {isAuthenticated ? (
                <>
                    <StyledWrapper {...styles.priceContainer}>
                        {msrpValueToDisplay !== null && (
                            <>
                                {isAuthenticated && (msrpValueToDisplay === "false" || !msrpValueToDisplay) && (
                                    <>
                                        <ProductPrice
                                            product={productContext}
                                            showLabel={true}
                                            showSavings={true}
                                            showSavingsAmount={productSettings.showSavingsAmount}
                                            showSavingsPercent={productSettings.showSavingsPercent}
                                            extendedStyles={styles.price}
                                        />
                                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                                    </>
                                )}
                                {isAuthenticated && msrpValueToDisplay === "true" && (
                                    <Typography as="p" {...styles.valueTextWarp}>
                                        <Typography {...styles.valueTextNormal}>
                                            {session.currency?.currencySymbol}
                                            {productContext.product.properties.msrp}
                                        </Typography>
                                    </Typography>
                                )}
                            </>
                        )}
                        <ProductContextAvailability cartLine={cartLine} extendedStyles={styles.availabilityStyles} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.addToCartWrapper}>
                        <StyledWrapper>
                            <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.btnWrapper}>
                            <ProductAddToCartButton
                                data-test-selector={`actionsAddToCart${productContext.product.id}`}
                                extendedStyles={styles.addToCartButton}
                            />
                        </StyledWrapper>
                        <ProductInStockNotificationButton
                            data-test-selector={`actionsNotifyMe${productContext.product.id}`}
                            extendedStyles={styles.addToCartButton}
                        />
                        <StyledWrapper>
                            {showAddToList && (
                                <StyledWrapper {...styles.addToListWrapper}>
                                    <ProductAddToListLink
                                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                                        extendedStyles={styles.addToListLink}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            ) : (
                <StyledWrapper {...styles.addToCartWrapper}>
                    <ViewProductButton productDetail={productContext} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActionsListView));
